import { CheckCircle, Error } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Badge from '@mui/material/Badge';
import { useTheme } from '@mui/material/styles';
import { MouseEvent, useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Images } from '@/assets/images';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { TokenContext } from '@/Contexts/TokenContext';
import { UsersContext } from '@/Contexts/UsersContext';
import useAuth from '@/hooks/useAuth';
import useUser from '@/hooks/useUser';
import { ProgramNames } from '@/models/Program';
import { Locales, UserProps, UserType } from '@/models/User';

import { DialogTitleWithClose } from '../common/DialogTitleWithClose';
import AccountIcon from '../common/icons/AccountIcon';
import ProfileMenu from './ProfileMenu';
import AdminIcon from '../common/icons/AdminIcon';
import HouseIcon from '../common/icons/HouseIcon';
import QuestionIcon from '../common/icons/QuestionIcon';
import RecurringIcon from '../common/icons/RecurringIcon';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';

function Header() {
  const theme = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isCompactHeader = useMediaQuery(theme.breakpoints.down('md'));
  const { b2cId, validated } = useContext(TokenContext);

  const { usersAll, userType, admin, userCurrent, updateUserCurrent } = useContext(UsersContext);
  const { partnerGroupId: spPartnerGroupId, partnerInfo: spPartnerInfo } =
    useContext(SelectedPartnerContext);

  const { partnerGroupId: caPartnerGroupId, partnerInfo: caPartnerInfo } =
    useContext(CurrentApplicationContext);
  const partnerGroupId = caPartnerGroupId ?? spPartnerGroupId;
  const partnerInfo = caPartnerInfo ?? spPartnerInfo;

  const { stopUserImpersonation, updatePreferredLanguage } = useUser();
  const { logoutCurrentUser } = useAuth();
  const [invisible, setInvisible] = useState<boolean>(true);

  const { locale, translatedText, updateLocale } = useContext(LocaleContext);

  const [instructionsModalOpen, setIntructionModalOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [accountAnchorEl, setAccountAnchorEl] = useState<null | HTMLElement>(null);
  const accountMenuOpen = Boolean(accountAnchorEl);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuTriggerEl, setMobileMenuTriggerEl] = useState<null | HTMLElement>(null);
  const mobileMenuOpen = Boolean(mobileMenuAnchorEl);

  const [impersonating, setImpersonating] = useState<boolean>(false);
  const [postingUser, setPostingUser] = useState<boolean>(false);

  useEffect(() => {
    if (translatedText == null) {
      updateLocale('enUS');
    }
  }, [locale, translatedText, updateLocale]);

  useEffect(() => {
    if (userType == UserType.Applicant && usersAll != null) {
      usersAll.forEach((i: UserProps) => {
        if (!i.validated && i.partnerGroupId == partnerGroupId && i.partnerGroupId != null) {
          setInvisible(false);
        }
      });
    }
  }, [userType, usersAll, partnerGroupId]);

  useEffect(() => {
    if (userCurrent != null) {
      const imp =
        userCurrent != null &&
        userCurrent.userType != UserType.Adf &&
        userCurrent.userAccountType == UserType.Adf;
      setImpersonating(imp);
    }
  }, [userCurrent]);

  const stopImpersonation = async () => {
    if (userCurrent?.userImpersonated == null || b2cId == null) return;

    setPostingUser(true);

    // reset the impersonating user info
    await stopUserImpersonation({
      impersonatorB2CId: b2cId,
      impersonateeB2CId: userCurrent.userImpersonated,
    });

    await logoutCurrentUser('Impersonation');

    setPostingUser(false);
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageSelect = async (event?: React.MouseEvent<HTMLDivElement>) => {
    const target = event?.target as HTMLElement | undefined;
    const localeValue = target?.getAttribute('value') as Locales | null;

    if (localeValue != null && b2cId != null) {
      await updatePreferredLanguage(localeValue);
      updateUserCurrent((prevUser) =>
        prevUser != null ? { ...prevUser, preferredLanguage: localeValue } : null
      );
      setAnchorEl(null);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccountClick = (event: MouseEvent<HTMLElement>) => {
    setAccountAnchorEl(event.currentTarget);
  };

  const handleAccountClose = () => {
    setAccountAnchorEl(null);
  };

  const handleMobileMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setMobileMenuTriggerEl(event.currentTarget);
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  const openAccountMenuFromMobile = () => {
    if (mobileMenuTriggerEl != null) {
      setAccountAnchorEl(mobileMenuTriggerEl);
    }
    setMobileMenuAnchorEl(null);
  };

  const openLanguageMenuFromMobile = () => {
    if (mobileMenuTriggerEl != null) {
      setAnchorEl(mobileMenuTriggerEl);
    }
    setMobileMenuAnchorEl(null);
  };

  function openInstructionsModal() {
    setIntructionModalOpen(true);
  }

  function handleModalClose() {
    setIntructionModalOpen(false);
  }

  const iconButtonSx = {
    color: 'text.primary',
    borderRadius: 0,
    aspectRatio: 1,
    '&[aria-current]': {
      backgroundColor: theme.palette.sandShade2.main,
    },
    '&:hover': {
      backgroundColor: theme.palette.sandShade1.main,
    },
  };

  const userFullName =
    `${userCurrent?.contact?.firstName ?? ''} ${userCurrent?.contact?.lastName ?? ''}`.trim();

  const showLanguageSelector =
    userType == UserType.Applicant &&
    partnerInfo != null &&
    partnerInfo.programId == ProgramNames.TreeCitiesWorld;

  const showUserManagement = !(
    userType == UserType.RegionalCoordinator ||
    userType == UserType.NationalProgrammeLead ||
    (userType == UserType.Applicant && (!admin || !validated))
  );

  return userCurrent == null ? (
    <></>
  ) : (
    <Box
      display='flex'
      alignItems='center'
      justifyContent='space-between'
      sx={{
        background: theme.palette.sandTint.main,
        height: { xs: '56px', md: '50px' },
        boxShadow: '0px 0px 8px 0px #0A0A0A1F',
        px: { xs: 1.25, sm: 2, md: 6, lg: 12, xl: 23 },
      }}
      zIndex={100}
      maxHeight={{ xs: '56px', md: '50px' }}
      minHeight={{ xs: '56px', md: '50px' }}
    >
      <Dialog open={instructionsModalOpen} onClose={handleModalClose}>
        <DialogTitleWithClose onClose={handleModalClose}>Instructions</DialogTitleWithClose>
        <DialogContent>
          <Typography>
            In order to view the signature page, please answer all required questions on the
            previous standards.
          </Typography>
          <Divider />
          <Box display='flex' gap='10px' alignItems='center'>
            <CheckCircle
              sx={{
                fill: theme.palette.forestTint1.main,
                strokeWidth: '4px',
                strokeLinejoin: 'round',
                paintOrder: 'stroke',
                height: '1.5em',
                width: '1.5em',
                opacity: 1,
                fillOpacity: 1,
                color: theme.palette.background.default,
              }}
            />
            <Typography>
              Standard you are currently on and all required questions are answered.
            </Typography>
          </Box>
          <Box display='flex' gap='10px' alignItems='center'>
            <CheckCircle
              sx={{
                stroke: theme.palette.forestTint1.main,
                strokeWidth: '4px',
                strokeLinejoin: 'round',
                paintOrder: 'stroke',
                height: '1.25em',
                width: '1.5em',
                opacity: 1,
                fillOpacity: 1,
                color: theme.palette.background.default,
              }}
            />
            <Typography>All required questions are answered for this standard.</Typography>
          </Box>
          <Box display='flex' gap='10px' alignItems='center'>
            <Error
              sx={{
                fill: theme.palette.warning.main,
                strokeWidth: '4px',
                strokeLinejoin: 'round',
                paintOrder: 'stroke',
                height: '1.5em',
                width: '1.5em',
              }}
            />
            <Typography>
              Standard you are currently on and all required questions are NOT answered.
            </Typography>
          </Box>
          <Box display='flex' gap='10px' alignItems='center'>
            <Error
              sx={{
                stroke: theme.palette.warning.main,
                strokeWidth: '4px',
                strokeLinejoin: 'round',
                paintOrder: 'stroke',
                height: '1.25em',
                width: '1.5em',
                opacity: 1,
                fillOpacity: 1,
                color: theme.palette.background.default,
              }}
            />
            <Typography>All required questions are NOT answered for this standard.</Typography>
          </Box>
        </DialogContent>
      </Dialog>

      <Box
        sx={{
          width: { xs: 'clamp(130px, 55vw, 210px)', md: '300px' },
          textAlign: 'left',
          mr: { xs: 0.75, md: 2.5 },
          lineHeight: 0,
          flexShrink: 1,
        }}
      >
        <img height='30px' src={Images.ArborDayLogoWide} alt='logo' />
      </Box>

      {isCompactHeader ? (
        <Box component='nav' display='flex' alignItems='center' flexShrink={0}>
          <IconButton
            aria-label='Open menu'
            aria-controls={mobileMenuOpen ? 'mobile-header-menu' : undefined}
            aria-haspopup='true'
            aria-expanded={mobileMenuOpen ? 'true' : undefined}
            sx={{ ...iconButtonSx }}
            onClick={handleMobileMenuOpen}
          >
            <MenuIcon />
          </IconButton>

          <Menu
            id='mobile-header-menu'
            anchorEl={mobileMenuAnchorEl}
            open={mobileMenuOpen}
            onClose={handleMobileMenuClose}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          >
            {impersonating && (
              <MenuItem
                onClick={() => {
                  void stopImpersonation();
                  handleMobileMenuClose();
                }}
                disabled={postingUser}
              >
                <ListItemIcon>
                  {postingUser ? <CircularProgress size='18px' /> : <RecurringIcon />}
                </ListItemIcon>
                <ListItemText>Stop Impersonating User</ListItemText>
              </MenuItem>
            )}

            {validated && (
              <MenuItem
                component={Link}
                to='/'
                selected={pathname === '/' || pathname === '/dashboard'}
                onClick={handleMobileMenuClose}
              >
                <ListItemIcon>
                  <HouseIcon />
                </ListItemIcon>
                <ListItemText>{translatedText.header.tooltipDashboard}</ListItemText>
              </MenuItem>
            )}

            {validated && pathname.includes('app') && (
              <MenuItem
                onClick={() => {
                  openInstructionsModal();
                  handleMobileMenuClose();
                }}
              >
                <ListItemIcon>
                  <InfoOutlinedIcon />
                </ListItemIcon>
                <ListItemText>Instructions</ListItemText>
              </MenuItem>
            )}

            {showLanguageSelector && (
              <MenuItem onClick={openLanguageMenuFromMobile}>
                <ListItemIcon>
                  <LanguageIcon />
                </ListItemIcon>
                <ListItemText>{translatedText.header.tooltipLanguage}</ListItemText>
              </MenuItem>
            )}

            {showUserManagement && (
              <MenuItem
                component={Link}
                to='/usermanagement'
                selected={pathname === '/usermanagement'}
                onClick={handleMobileMenuClose}
              >
                <ListItemIcon>
                  <Badge color='error' variant='dot' invisible={invisible}>
                    <AdminIcon />
                  </Badge>
                </ListItemIcon>
                <ListItemText>{translatedText.header.tooltipUserManagement}</ListItemText>
              </MenuItem>
            )}

            <MenuItem
              component='a'
              href='https://www.arborday.org/faqs/recognition-program'
              target='_blank'
              rel='noopener noreferrer'
              onClick={handleMobileMenuClose}
            >
              <ListItemIcon>
                <QuestionIcon />
              </ListItemIcon>
              <ListItemText>{translatedText.header.tooltipHelp}</ListItemText>
            </MenuItem>

            {validated && (
              <MenuItem
                onClick={() => {
                  navigate('/myaccount');
                  handleMobileMenuClose();
                }}
                selected={pathname === '/myaccount'}
              >
                <ListItemIcon>
                  <AccountIcon />
                </ListItemIcon>
                <ListItemText>{translatedText.header.tooltipMyAccount}</ListItemText>
              </MenuItem>
            )}

            {validated && (
              <MenuItem onClick={openAccountMenuFromMobile}>
                <ListItemIcon>
                  <AccountIcon />
                </ListItemIcon>
                <ListItemText>Account Menu</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </Box>
      ) : (
        <Box
          component='nav'
          display='flex'
          marginRight='10px'
          alignItems='stretch'
          flexShrink={0}
          sx={{
            alignSelf: 'stretch',
          }}
        >
          {impersonating &&
            (postingUser ? (
              <Box
                sx={{
                  ...iconButtonSx,
                  justifyContent: 'center',
                  alignContent: 'center',
                  display: 'flex',
                  flexWrap: 'wrap',
                }}
              >
                <CircularProgress size='20px'></CircularProgress>
              </Box>
            ) : (
              <Tooltip title={translatedText.header.tooltipStopImpersonation}>
                <IconButton
                  aria-label={translatedText.header.tooltipStopImpersonation}
                  sx={{
                    ...iconButtonSx,
                  }}
                  onClick={stopImpersonation}
                >
                  <RecurringIcon />
                </IconButton>
              </Tooltip>
            ))}

          {validated && (
            <Tooltip title={translatedText.header.tooltipDashboard}>
              <IconButton
                component={Link}
                to='/'
                aria-label='Return to dashboard'
                aria-current={pathname === '/' || pathname === '/dashboard' ? 'page' : undefined}
                sx={{
                  ...iconButtonSx,
                }}
              >
                <HouseIcon />
              </IconButton>
            </Tooltip>
          )}

          {validated && pathname.includes('app') && (
            <Tooltip title={translatedText.header.instructionsTitle}>
              <IconButton
                aria-label={translatedText.header.instructionsTitle}
                aria-haspopup='dialog'
                aria-expanded={instructionsModalOpen ? 'true' : 'false'}
                sx={{
                  ...iconButtonSx,
                }}
                onClick={openInstructionsModal}
              >
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}

          {showLanguageSelector ? (
            <>
              <Tooltip title={translatedText.header.tooltipLanguage}>
                <IconButton
                  aria-label={translatedText.header.tooltipLanguage}
                  aria-controls={open ? 'account-menu' : undefined}
                  aria-haspopup='true'
                  aria-expanded={open ? 'true' : undefined}
                  sx={{
                    ...iconButtonSx,
                  }}
                  onClick={handleClick}
                >
                  <LanguageIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <></>
          )}

          {showUserManagement ? (
            <Tooltip title={translatedText.header.tooltipUserManagement}>
              <IconButton
                component={Link}
                to='/usermanagement'
                aria-label={translatedText.header.tooltipUserManagement}
                aria-current={location.pathname === '/usermanagement' ? 'page' : undefined}
                sx={{
                  ...iconButtonSx,
                }}
              >
                <Badge color='error' variant='dot' invisible={invisible}>
                  <AdminIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          ) : (
            <></>
          )}

          <Tooltip title={translatedText.header.tooltipHelp}>
            <IconButton
              component='a'
              href='https://www.arborday.org/faqs/recognition-program'
              target='_blank'
              rel='noopener noreferrer'
              aria-label={translatedText.header.tooltipHelp}
              sx={{
                ...iconButtonSx,
              }}
            >
              <QuestionIcon />
            </IconButton>
          </Tooltip>

          {validated ? (
            <>
              <Tooltip
                title={
                  <span>
                    <Typography variant='subtitle1' fontWeight={'bold'}>
                      {translatedText.header.tooltipMyAccount}
                    </Typography>
                    <Typography variant='subtitle3'>{userFullName}</Typography>
                    <Typography variant='subtitle3'>{userCurrent.email}</Typography>
                  </span>
                }
              >
                <IconButton
                  aria-label={`My account\n${userFullName}\n${userCurrent.email}`}
                  aria-controls={accountMenuOpen ? 'account-dropdown-menu' : undefined}
                  aria-haspopup='true'
                  aria-expanded={accountMenuOpen ? 'true' : undefined}
                  aria-current={location.pathname === '/myaccount' ? 'page' : undefined}
                  sx={{ ...iconButtonSx }}
                  onClick={handleAccountClick}
                >
                  <AccountIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <></>
          )}
        </Box>
      )}

      <Menu
        sx={{
          boxShadow: 'none',
          '.MuiOutlinedInput-notchedOutline': { border: 0 },
        }}
        anchorEl={anchorEl}
        id='account-menu'
        open={open}
        onClose={handleClose}
        onClick={handleLanguageSelect}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem value='enUS' key='enUS' selected={locale === 'enUS'}>
          English
        </MenuItem>
        <MenuItem value='esES' key='esES' selected={locale === 'esES'}>
          Español
        </MenuItem>
        <MenuItem value='frFR' key='frFR' selected={locale === 'frFR'}>
          Français
        </MenuItem>
        <MenuItem value='ptBR' key='ptBR' selected={locale === 'ptBR'}>
          Português
        </MenuItem>
        <MenuItem value='itIT' key='itIT' selected={locale === 'itIT'}>
          Italiano
        </MenuItem>
      </Menu>

      <ProfileMenu anchorEl={accountAnchorEl} open={accountMenuOpen} onClose={handleAccountClose} />
    </Box>
  );
}

export default Header;
